import { useEffect, useState } from 'react';
import {
  type ActivityQualification,
  type BoardActivity,
  type Employee,
} from '@lemontaigu/staff';
import { useRoster } from '@lemontaigu/staff/react';
import { DayView } from './DayView.js';
import { WeekView } from './WeekView.js';
import { MonthView } from './MonthView.js';
import { useBoard } from '@lemontaigu/staff/react';
import { useCapability } from '@lemontaigu/platform-ui';
import { CAPABILITIES } from '@lemontaigu/platform';
import { BoardProvider } from './BoardProvider.js';

// « Planning » (S4): the desk's board in three horizons — Jour (demand
// gesture + coverage + declared-vs-clocked), Semaine (weekly load input +
// the Gantt-like activity board + gap broadcast), Mois (read-only calendar
// projection). The activity matrix / qualifications / roster are config —
// loaded once here and shared by all three views; each view loads its own
// date-ranged data.

type Mode = 'jour' | 'semaine' | 'mois';

const MODES: { mode: Mode; label: string }[] = [
  { mode: 'jour', label: 'Jour' },
  { mode: 'semaine', label: 'Semaine' },
  { mode: 'mois', label: 'Mois' },
];

/**
 * Composition root for the team-board subtree (ladder step 6).
 *
 * `Planning` is what BOTH cockpits mount — reception.* directly and bos.* via
 * the published `@lemontaigu/reception/team/Planning.js` entry — so binding the
 * BoardStore here means one wiring point serves both, rather than each app's
 * main.tsx repeating it.
 */
export function Planning(props: { showHours?: boolean } = {}) {
  return (
    <BoardProvider>
      <PlanningViews {...props} />
    </BoardProvider>
  );
}

function PlanningViews({
  showHours = false,
}: {
  /**
   * Also show each day's actual worked hours per employee in the Présence
   * matrix — bos@ only (reception@ omits it, unchanged). Started on the Mois
   * tab (2026-07-19); the Jour and Semaine matrices honour it too since
   * 2026-08-04, so the flag now means the same thing on all three horizons.
   */
  showHours?: boolean;
} = {}) {
  const [mode, setMode] = useState<Mode>('jour');
  const [focusDay, setFocusDay] = useState<string | null>(null);
  const [activities, setActivities] = useState<BoardActivity[] | null>(null);
  const [qualifications, setQualifications] = useState<ActivityQualification[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  // Who may pin ★ assignments and remove any presence.
  //
  // Ladder step 8 replaced a `supabase.rpc('is_admin')` probe here with the
  // capability set fetched once per session, but deliberately kept reading the
  // COARSE `platform.admin` — because the policy behind the write still tested
  // `is_admin()`, and asking the finer question first would have shown this
  // control to a session the database would then refuse.
  //
  // Step 9 (migration 0063) moved `bap_admin_all` onto `rh.board.assign`, so
  // the two now move together, in the same change, as promised. The capability
  // is held by the operator and by the « planner » access MODE — so once a
  // client asserts `X-Capability-Mode: planner`, the desk gains ★ assignment
  // here AND in the database, from one grant row and with no deploy. Until a
  // client sends that header, this resolves exactly as `platform.admin` did.
  const canAssign = useCapability(CAPABILITIES.RH_BOARD_ASSIGN);
  const [err, setErr] = useState('');
  const board = useBoard();
  const roster = useRoster();

  useEffect(() => {
    void (async () => {
      try {
        const [acts, quals, emps] = await Promise.all([
          board.activities(),
          board.qualifications(),
          roster.current(),
        ]);
        setActivities(acts);
        setQualifications(quals);
        setEmployees(emps);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [board, roster]);

  if (err) return <p className="auth-err">Erreur : {err}</p>;
  if (activities === null) return <p className="staff-empty">Chargement…</p>;

  const openDay = (day: string) => {
    setFocusDay(day);
    setMode('jour');
  };

  // Untimed activities (« sans horaire ») render only on the Jour board (a chip
  // row after the Gantt). The Semaine/Mois views are pure hour-axis Gantts —
  // a windowless activity has no bar there, so they get the timed subset only.
  const timedActivities = activities.filter((a) => !a.untimed);

  return (
    <div>
      <div className="planning-modes">
        {MODES.map((m) => (
          <button
            key={m.mode}
            type="button"
            className={mode === m.mode ? 'is-active' : ''}
            onClick={() => {
              if (m.mode !== 'jour') setFocusDay(null);
              setMode(m.mode);
            }}
          >
            {m.label}
          </button>
        ))}
      </div>
      {mode === 'jour' && (
        <DayView
          key={focusDay ?? 'default'}
          activities={activities}
          qualifications={qualifications}
          employees={employees}
          initialDay={focusDay}
          canAssign={canAssign}
          showHours={showHours}
        />
      )}
      {mode === 'semaine' && (
        <WeekView
          activities={timedActivities}
          qualifications={qualifications}
          employees={employees}
          canAssign={canAssign}
          showHours={showHours}
        />
      )}
      {mode === 'mois' && (
        <MonthView
          activities={timedActivities}
          qualifications={qualifications}
          employees={employees}
          onOpenDay={openDay}
          showHours={showHours}
          canAssign={canAssign}
        />
      )}
    </div>
  );
}
